import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { ApiResponse, Client, ClientRole, KeycloakClient, UserWithRoles } from '../models/client.model';
import { StorageService } from './storage.service';
import { environment } from '../../../environments/environment';
import { PaginatedResponse } from '../models/api-response.model';
import { PaginatedRoleResponse } from '../models/role.model';

/** DTO renvoyé par /clients/by-types/with-roles (miroir de ClientRolesTDO Java) */
export interface ClientRolesTDO {
  id: string;
  clientId: string;
  roles: { id: string; name: string; title: string }[];
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private apiUrl = `${environment.apiUrl}/clients`;
  private rolesApiUrl = `${environment.apiUrl}/roles`;

  // BehaviorSubject pour gérer l'état du client actif
  private activeClientSubject = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient, private storageService: StorageService) {
    // Initialiser avec la valeur du localStorage au démarrage
    const storedClient = this.storageService.getItem('activeClient');
    if (storedClient) {
      this.activeClientSubject.next(storedClient);
    }
  }

  /**
   * Appelle GET /clients/by-types/with-roles avec la liste de types fournie.
   *
   * Le backend filtre automatiquement les rôles selon le profil contenu dans
   * le token JWT de l'utilisateur connecté :
   *   - SUPER_ADMIN → tous les rôles
   *   - IES_ADMIN   → uniquement les rôles dont le nom contient "IES_"
   *
   * À utiliser depuis le composant d'attribution de rôles en lui passant :
   *   - ['external-app']                   pour IES_ADMIN
   *   - ['external-app', 'internal-app']   pour SUPER_ADMIN
   *
   * @param types Liste des types applicatifs (au moins un requis)
   * @returns Observable<ClientRolesTDO[]> — clients avec rôles déjà filtrés
   */
  getClientsByTypesWithRoles(types: string[]): Observable<ClientRolesTDO[]> {
    let params = new HttpParams();

    types.forEach(t => (params = params.append('types', t)));

    return this.http
      .get<ClientRolesTDO[]>(`${this.apiUrl}/by-types/with-roles`, { params })
      .pipe(catchError(this.handleError));
  }

  getClientsByUser(userId: string): Observable<Client[]> {
    return this.http.get<Client[]>(`${this.apiUrl}/by-realm/sigesr`);
  }

  getAllClientsAndRoles(): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this.apiUrl}/allClientsAndRoles`);
  }

  /**
   * Récupère les clients par leurs noms logiques, avec leurs rôles filtrés
   * selon le profil de l'utilisateur connecté.
   */
  getClientsByClientLisWithRoles(logicalIds: string[]): Observable<ClientRole[]> {
    let params = new HttpParams();
    logicalIds.forEach(name => {
      params = params.append('names', name);
    });
    return this.http
      .get<ClientRole[]>(`${this.apiUrl}/by-client-ids/with-roles`, { params })
      .pipe(catchError(this.handleError));
  }

  /** Définit le client actif */
  setActiveClient(clientId: string): void {
    this.storageService.setItem('activeClient', clientId);
    this.activeClientSubject.next(clientId);
  }

  /** Récupère le client actif sous forme d'Observable */
  getActiveClient(): Observable<string | null> {
    return this.activeClientSubject.asObservable();
  }

  /** Récupère la valeur actuelle du client actif (synchrone) */
  getCurrentActiveClient(): string | null {
    return this.activeClientSubject.value;
  }

  clearActiveClient(): void {
    this.storageService.removeItem('activeClient');
    this.activeClientSubject.next(null);
  }

  /**
 * Récupère une liste de clients à partir de leurs UUID Keycloak.
 * Appelle GET /clients/by-ids?ids=id1&ids=id2
 */
  getClientsByIds(ids: string[]): Observable<KeycloakClient[]> {
    let params = new HttpParams();
    ids.forEach(id => (params = params.append('ids', id)));
    return this.http
      .get<KeycloakClient[]>(`${this.apiUrl}/by-ids`, { params })
      .pipe(catchError(this.handleError));
  }

  /**
 * Récupère la liste complète des clients dont le type est "external-app" ou "internal-app".
 * Utilise l'endpoint /by-types/with-roles puis /by-ids pour enrichir les données.
 */
  getApplicationClients(
    types: string[] = ['external-app', 'internal-app']
  ): Observable<KeycloakClient[]> {
    return this.getClientsByTypesWithRoles(types).pipe(
      switchMap((clientRoles: ClientRolesTDO[]) => {
        if (!clientRoles.length) {
          return of([]);
        }
        const ids = clientRoles.map(cr => cr.id);
        return this.getClientsByIds(ids);
      }),
      catchError((error) => {
        console.error('Erreur lors du chargement des applications :', error);
        return throwError(() => new Error('Impossible de charger les applications'));
      })
    );
  }

  /** Récupère tous les clients avec pagination */
  getAllClients(
    first: number = 0,
    max: number = 20,
    search?: string
  ): Observable<PaginatedResponse<KeycloakClient>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<PaginatedResponse<KeycloakClient>>(`${this.apiUrl}/all`, { params })
      .pipe(catchError(this.handleError));
  }

  /** Récupère un client par son ID */
  getClient(clientId: string): Observable<KeycloakClient> {
    return this.http
      .get<KeycloakClient>(`${this.apiUrl}/${clientId}`)
      .pipe(catchError(this.handleError));
  }

  /** Crée un nouveau client */
  createClient(client: Partial<KeycloakClient>): Observable<KeycloakClient> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .post<KeycloakClient>(this.apiUrl, client, { headers })
      .pipe(catchError(this.handleError));
  }

  /** Met à jour un client existant */
  updateClient(clientId: string, client: Partial<KeycloakClient>): Observable<KeycloakClient> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .put<KeycloakClient>(`${this.apiUrl}/${clientId}`, client, { headers })
      .pipe(catchError(this.handleError));
  }

  /** Supprime un client */
  deleteClient(clientId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiUrl}/${clientId}`)
      .pipe(catchError(this.handleError));
  }

  /** Régénère le secret d'un client */
  regenerateClientSecret(clientId: string): Observable<{ secret: string }> {
    return this.http
      .post<{ secret: string }>(`${this.apiUrl}/${clientId}/regenerate-secret`, {})
      .pipe(catchError(this.handleError));
  }

  /**
   * Récupère les rôles d'un client spécifique (endpoint /roles/).
   * Conservé pour les autres écrans qui en ont encore besoin.
   */
  getClientRoles(
    clientId: string,
    first: number = 0,
    max: number = 50,
    search?: string
  ): Observable<PaginatedRoleResponse> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http
      .get<PaginatedRoleResponse>(`${this.rolesApiUrl}/client-roles/${clientId}`, { params })
      .pipe(catchError(this.handleError));
  }

  /** Vérifie l'état de santé du service */
  checkHealth(): Observable<{ status: string; service: string; timestamp: string }> {
    return this.http
      .get<{ status: string; service: string; timestamp: string }>(`${this.apiUrl}/health`)
      .pipe(catchError(this.handleError));
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = "Une erreur inconnue s'est produite";

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Erreur client: ${error.error.message}`;
    } else {
      switch (error.status) {
        case 400: errorMessage = 'Données invalides'; break;
        case 401: errorMessage = 'Non autorisé'; break;
        case 403: errorMessage = 'Accès refusé'; break;
        case 404: errorMessage = 'Ressource non trouvée'; break;
        case 409: errorMessage = 'Le client existe déjà'; break;
        case 500: errorMessage = 'Erreur serveur interne'; break;
        default: errorMessage = `Erreur serveur: ${error.status} - ${error.message}`;
      }

      if (error.error?.message) {
        errorMessage += ` - ${error.error.message}`;
      }
    }

    console.error('Erreur API Client:', error);
    return throwError(() => new Error(errorMessage));
  }

  getUsersWithRolesForClient(
    clientId: string,
    first: number = 0,
    max: number = 20,
    role?: string,
    search?: string  
  ): Observable<PaginatedResponse<UserWithRoles>> {
    let params = new HttpParams()
      .set('first', first.toString())
      .set('max', max.toString());

    if (role && role.trim()) params = params.set('role', role.trim());

    if (search && search.trim()) params = params.set('search', search.trim());

    return this.http
      .get<PaginatedResponse<UserWithRoles>>(`${this.apiUrl}/${clientId}/users`, { params })
      .pipe(catchError(this.handleError));
  }
}